import { useTranslation } from "react-i18next";
import Card from "src/components/Card";
import Loader from "src/components/Loader";
import ReadMore from "src/components/ReadMore";
import ResumeBadges from "src/components/resume/modules/ResumeBadges";
import ResumeExperiencesList from "src/components/resume/modules/ResumeExperiencesList";
import ResumeReviews from "src/components/resume/modules/ResumeReviews";
import ResumeSkills from "src/components/resume/modules/ResumeSkills";
import ResumeSocialMedia from "src/components/resume/modules/ResumeSocialMedia";
import ResumeHeader from "src/components/resume/ResumeHeader";
import useGet from "src/hooks/rest/useGet";
import RegistrationSection from "src/components/tasks/panes/registration/RegistrationSection";

interface ResumeProps {
	id: string;
	exclude: string[];
	variant: "default" | "registration";
}

const Resume = ({ id, exclude, variant }: ResumeProps) => {
	const { t } = useTranslation("resume", {
		keyPrefix: "resume",
	});
	const [{ data: user }, loading] = useGet(`/resume/${id}/basic-info`);
	const color = user?.primary_color || "";
	const modules = user?.resume_modules || [];

	const checkModule = (name: string) => {
		return modules.includes(name) && !exclude.includes(name);
	};

	if (!user && loading) {
		return (
			<div className="flex flex-col flex-1 justify-center items-center">
				<Loader />
			</div>
		);
	}

	if (!user) {
		return (
			<div className="flex flex-col flex-1 justify-center items-center">
				<strong className="text-dark">{t("empty.title")}</strong>
			</div>
		);
	}

	if (variant === "registration") {
		return (
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				{checkModule("bio") && (
					<RegistrationSection
						className="flex-1"
						title={t("titles.bio")}
					>
						<ReadMore text={user?.bio?.bio || ""} length={240} />
					</RegistrationSection>
				)}
				{checkModule("experiences.education") && (
					<RegistrationSection
						className="flex-1"
						title={t("titles.education")}
					>
						<ResumeExperiencesList
							type="education"
							query={`/resume/${id}/experiences?type=education`}
							className=""
							{...{ color }}
						/>
					</RegistrationSection>
				)}
				{checkModule("social-media") && (
					<RegistrationSection
						className="flex-1"
						title={t("titles.social")}
					>
						<ResumeSocialMedia
							query={`/resume/${id}/social-media`}
						/>
					</RegistrationSection>
				)}
				{checkModule("experiences.job") && (
					<RegistrationSection
						className="flex-1"
						title={t("titles.job")}
					>
						<ResumeExperiencesList
							type="job"
							query={`/resume/${id}/experiences?type=job`}
							className=""
							{...{ color }}
						/>
					</RegistrationSection>
				)}
				{checkModule("reviews") && (
					<div className="md:col-span-2">
						<RegistrationSection
							className="bg-white"
							title={t("titles.reviews")}
						>
							<ResumeReviews
								columns={2}
								query={`/resume/${id}/reviews`}
							/>
						</RegistrationSection>
					</div>
				)}
			</div>
		);
	}

	return (
		<>
			{!exclude?.includes("header") && (
				<Resume.Header
					backgroundColor={color || "#1aaa9d"}
					avatar={user.avatar}
					name={user.full_name}
					description={user?.bio?.bio}
					rating={user?.review}
				/>
			)}
			{checkModule("experiences.education") && (
				<Card id="education" title={t("titles.education")}>
					<ResumeExperiencesList
						type="education"
						query={`/resume/${id}/experiences?type=education`}
						{...{ color }}
					/>
				</Card>
			)}

			{checkModule("experiences.job") && (
				<Card id="jobs" title={t("titles.job")}>
					<ResumeExperiencesList
						{...{ color }}
						type="job"
						query={`/resume/${id}/experiences?type=job`}
					/>
				</Card>
			)}
			{checkModule("skills") && (
				<Card id="skills" title={t("titles.skills")}>
					<div className="mt-4">
						<ResumeSkills
							variant="small"
							query={`/resume/${id}/skills`}
						/>
					</div>
				</Card>
			)}
			{checkModule("social-media") && (
				<Card id="social-media" title={t("titles.social")}>
					<div className="mt-4">
						<ResumeSocialMedia
							query={`/resume/${id}/social-media`}
						/>
					</div>
				</Card>
			)}
			<Card id="badges" title={t("titles.badges")}>
				<div className="mt-4">
					<ResumeBadges query={`/resume/${id}/badges`} />
				</div>
			</Card>
			{checkModule("reviews") && (
				<Card id="reviews" title={t("titles.reviews")}>
					<div className="mt-4">
						<ResumeReviews
							columns={2}
							query={`/resume/${id}/reviews`}
						/>
					</div>
				</Card>
			)}
		</>
	);
};

Resume.defaultProps = {
	exclude: [],
	variant: "default",
};

Resume.Header = ResumeHeader;

export default Resume;
